import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import ScrollToTopButton from "../components/scroll-to-top/ScrollToTopButton";
import GroupCreatePage from "./groups/GroupCreatePage";
import GroupHomeSection from "./groups/GroupHomeSection";
import GroupsDiscoveryView from "./groups/GroupsDiscoveryView";
import GroupsModalLayer from "./groups/GroupsModalLayer";
import useScrollToTopButton from "../components/scroll-to-top/useScrollToTopButton";
import useGroupsPageState from "./groups/useGroupsPageState";
import "../styles/pages/groups/GroupsSharedUi.css";
import "../styles/pages/groups/GroupsDiscoveryView.css";
import "../styles/pages/groups/GroupCreatePage.css";
import "../styles/pages/groups/GroupHomeSection.css";
import "../styles/pages/groups/GroupsModalLayer.css";

export default function Groups({
  user,
  users = [],
  openGroupRequest,
  onOpenUserProfile,
  onGroupsLoaded,
}) {
  const { t } = useTranslation();
  const { isVisible: showScrollToTop, scrollToTop } = useScrollToTopButton();
  const controller = useGroupsPageState({
    user,
    users,
    openGroupRequest,
    onOpenUserProfile,
    onGroupsLoaded,
    t,
  });
  const isMobileViewport =
    typeof window !== "undefined" ? window.innerWidth <= 768 : false;
  const isMobileDetailOpen = Boolean(isMobileViewport && controller.selectedGroup);
  const shouldKeepNavbarVisibleOnMobile = Boolean(
    isMobileViewport && (controller.selectedGroup || controller.activeGroupHome)
  );
  const shouldLockGroupsScroll = Boolean(
    controller.selectedGroup ||
      controller.peopleListGroup ||
      controller.fullCoverGroup ||
      controller.groupCoverUpdateTarget ||
      controller.groupMetadataEditTarget ||
      controller.confirmAction ||
      (isMobileViewport && controller.activeGroupHome)
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    if (!shouldLockGroupsScroll) {
      return undefined;
    }

    const { body, documentElement } = document;
    const scrollY = window.scrollY;
    const lockScrollY = shouldKeepNavbarVisibleOnMobile ? 0 : scrollY;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyPosition = body.style.position;
    const previousBodyTop = body.style.top;
    const previousBodyWidth = body.style.width;
    const previousHtmlOverflow = documentElement.style.overflow;

    if (shouldKeepNavbarVisibleOnMobile && scrollY !== 0) {
      window.scrollTo(0, 0);
    }

    documentElement.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${lockScrollY}px`;
    body.style.width = "100%";

    return () => {
      documentElement.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
      body.style.position = previousBodyPosition;
      body.style.top = previousBodyTop;
      body.style.width = previousBodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [shouldKeepNavbarVisibleOnMobile, shouldLockGroupsScroll]);

  if (!user) {
    return (
      <section className="groups-page">
        <div className="groups-empty-state">
          <h2>{t("groups")}</h2>
          <p>{t("groups_sign_in_prompt")}</p>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`groups-page${isMobileDetailOpen ? " groups-page--detail-open" : ""}`}
    >
      <div className="groups-page-content">
        <ScrollToTopButton
          isVisible={showScrollToTop}
          onClick={scrollToTop}
          label={t("scroll_to_top", { defaultValue: "Scroll to top" })}
        />
        {controller.isCreateOpen ? (
          <GroupCreatePage controller={controller} t={t} />
        ) : controller.activeGroupHome ? (
          <GroupHomeSection controller={controller} t={t} />
        ) : (
          <GroupsDiscoveryView controller={controller} t={t} />
        )}
      </div>
      <GroupsModalLayer controller={controller} t={t} />
    </section>
  );
}
